<template>
  <v-dialog v-model="dialog" max-width="700" scrollable persistent>
    <v-card>
      <v-card-title class="d-flex align-center px-4 pt-1 pb-0">
        <img v-if="providerConfig.logo" :src="providerConfig.logo" width="32" height="32" class="mr-3 flex-shrink-0" />
        <v-icon v-else size="32" class="mr-3 flex-shrink-0" :color="providerConfig.color">{{ providerConfig.icon }}</v-icon>
        <div style="min-width: 0">
          <div class="title font-weight-medium text-truncate">{{ pickerText.title }}</div>
          <div class="caption grey--text d-flex align-center">
            {{ selectHintText }}
            <v-tooltip v-if="allowedExtensions.length" bottom max-width="300">
              <template #activator="{ on, attrs }">
                <span v-on="on" v-bind="attrs" class="info-icon">i</span>
              </template>
              <span>{{ hintTooltip }}</span>
            </v-tooltip>
          </div>
        </div>
      </v-card-title>

      <v-card-text style="min-height: 400px; max-height: 520px; overflow-y: auto" class="px-4 pt-0 pb-4">
        <v-divider class="cloud-picker-band-divider" />
        <div
          v-if="(loading || connecting) && !oauthPending"
          class="d-flex justify-center align-center"
          style="min-height: 400px"
        >
          <div class="text-center">
            <v-progress-circular indeterminate color="primary" size="48" />
            <p class="mt-4 grey--text">
              {{ connecting ? pickerText.connecting : pickerText.loading }}
            </p>
          </div>
        </div>

        <div v-else-if="oauthPending" class="d-flex justify-center align-center flex-column" style="min-height: 300px">
          <img v-if="providerConfig.logo" :src="providerConfig.logo" width="64" height="64" />
          <v-icon v-else size="64" :color="providerConfig.color">{{ providerConfig.icon }}</v-icon>
          <p class="title mt-4 mb-2 text-center">
            {{ pickerText.oauth_pending_title }}
          </p>
          <p class="body-2 grey--text text-center mb-6" style="max-width: 380px">
            {{
              oauthCancelled
                ? pickerText.oauth_cancelled_description
                : $vuetify.lang.t("$vuetify.gpt_dataset.cloud_picker.oauth_pending_description")
            }}
          </p>
          <v-btn color="primary" :loading="retrying || popupOpen" @click="handleOAuthRetry">
            <v-icon left>{{ oauthCancelled ? "mdi-lock-open-outline" : "mdi-refresh" }}</v-icon>
            {{
              oauthCancelled
                ? pickerText.oauth_cancelled_retry
                : $vuetify.lang.t("$vuetify.gpt_dataset.cloud_picker.connected_retry")
            }}
          </v-btn>
        </div>

        <v-alert v-else-if="error" type="error" text class="mt-4">
          {{ error }}
          <div class="mt-2">
            <v-btn small text color="error" @click="fetchFiles()">
              <v-icon small left>mdi-refresh</v-icon>
              {{ $vuetify.lang.t("$vuetify.gpt_dataset.cloud_picker.retry_btn") }}
            </v-btn>
          </div>
        </v-alert>

        <div v-else>
          <div class="d-flex flex-wrap align-center py-4 cloud-picker-account-row" style="gap: 4px 10px">
            <v-menu v-if="accountOptions.length > 1" offset-y>
              <template #activator="{ on, attrs }">
                <div
                  class="d-flex align-center cloud-picker-account cloud-picker-account-switcher"
                  style="gap: 10px; cursor: pointer"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-avatar size="28" color="blue lighten-4" class="flex-shrink-0">
                    <v-img v-if="accountPicture" :src="accountPicture" @error="markAvatarBroken(accountId)" />
                    <span v-else class="caption font-weight-medium primary--text">{{ accountInitial }}</span>
                  </v-avatar>
                  <span class="body-2 text-truncate" style="min-width: 0">
                    {{ accountId }}
                  </span>
                  <v-icon small color="grey darken-1" class="flex-shrink-0">mdi-chevron-down</v-icon>
                </div>
              </template>
              <v-list dense>
                <v-list-item v-for="acc in accounts" :key="acc.user_id" @click="switchAccount(acc.user_id)">
                  <v-list-item-icon class="mr-2">
                    <v-icon v-if="acc.user_id === accountId" small color="primary">mdi-check</v-icon>
                  </v-list-item-icon>
                  <v-avatar size="22" color="blue lighten-4" class="mr-2 flex-shrink-0">
                    <v-img v-if="pictureFor(acc)" :src="pictureFor(acc)" @error="markAvatarBroken(acc.user_id)" />
                    <span v-else class="caption font-weight-medium primary--text">
                      {{ String(acc.user_id || "?").charAt(0).toUpperCase() }}
                    </span>
                  </v-avatar>
                  <v-list-item-title>{{ acc.user_id }}</v-list-item-title>
                  <v-list-item-action class="my-0">
                    <v-btn icon x-small @click.stop="disconnectAccount(acc.user_id)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>
            <div v-else class="d-flex align-center cloud-picker-account" style="gap: 10px">
              <v-avatar size="28" color="blue lighten-4" class="flex-shrink-0">
                <v-img v-if="accountPicture" :src="accountPicture" @error="markAvatarBroken(accountId)" />
                <span v-else class="caption font-weight-medium primary--text">{{ accountInitial }}</span>
              </v-avatar>
              <span class="body-2 text-truncate" style="min-width: 0">
                {{ accountId }}
              </span>
              <v-btn icon x-small class="flex-shrink-0" @click="disconnectAccount(accountId)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
            <v-btn
              v-if="!$vuetify.breakpoint.xs"
              small
              text
              color="primary"
              class="text-none"
              @click="addAnotherAccount"
            >
              <v-icon small left class="cloud-picker-add-account-icon">
                mdi-account-plus-outline
              </v-icon>
              {{ pickerText.add_account }}
            </v-btn>
            <v-btn
              v-else
              icon
              small
              color="primary"
              class="flex-shrink-0"
              :title="pickerText.add_account"
              @click="addAnotherAccount"
            >
              <v-icon small>mdi-account-plus-outline</v-icon>
            </v-btn>
          </div>

          <v-divider class="cloud-picker-band-divider" />

          <div class="d-flex align-center py-2 cloud-picker-toolbar">
            <div class="d-flex align-center cloud-picker-breadcrumb" style="min-width: 0">
              <v-icon small color="grey darken-1" class="mr-2 flex-shrink-0">mdi-folder-outline</v-icon>
              <span
                v-for="(crumb, i) in displayCrumbs"
                :key="crumb.key"
                class="d-inline-flex align-center"
                :class="{ 'flex-shrink-0': crumb.index === 0 }"
                style="min-width: 0"
              >
                <span
                  class="body-2 text-truncate cloud-picker-crumb"
                  :class="{
                    'cloud-picker-crumb--link primary--text': !crumb.last,
                    'cloud-picker-crumb--root': crumb.index === 0,
                  }"
                  :title="crumb.title"
                  @click="!crumb.last && navigateTo(crumb.index)"
                >{{ crumb.name }}</span>
                <v-icon v-if="i < displayCrumbs.length - 1" x-small color="grey" class="mx-1 flex-shrink-0">
                  mdi-chevron-right
                </v-icon>
              </span>
            </div>
            <div class="d-flex align-center ml-auto flex-shrink-0 cloud-picker-actions">
            <div v-if="showSearch" class="cloud-picker-searchbox mr-1">
              <svg
                class="ml-2 mr-1 flex-shrink-0"
                width="15"
                height="15"
                viewBox="0 0 16 16"
                fill="none"
              >
                <circle cx="7" cy="7" r="4.6" stroke="#757575" stroke-width="1.6" />
                <line
                  x1="10.6"
                  y1="10.6"
                  x2="13.4"
                  y2="13.4"
                  stroke="#757575"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="pickerText.search_placeholder"
                class="cloud-picker-searchbox-input"
              />
              <v-icon
                v-if="searchQuery"
                size="14"
                color="grey"
                class="cloud-picker-searchbox-clear mr-2 flex-shrink-0"
                @click="searchQuery = ''"
              >
                mdi-close
              </v-icon>
            </div>
            <v-btn
              icon
              class="flex-shrink-0"
              :disabled="loading || refreshing"
              :title="pickerText.refresh_btn"
              @click="refreshFiles"
            >
              <v-icon :class="{ 'cloud-picker-refresh-spin': refreshing }">mdi-autorenew</v-icon>
            </v-btn>
            </div>
          </div>

          <v-divider class="cloud-picker-band-divider mb-3" />

          <div v-if="treeItems.length === 0" class="d-flex justify-center align-center" style="min-height: 300px">
            <div class="text-center grey--text" style="max-width: 390px">
              <v-icon size="48" color="grey lighten-1">mdi-folder-open-outline</v-icon>
              <p class="mt-2 mb-0">
                {{ allowedExtensions.length ? emptyFilteredText : pickerText.empty }}
              </p>
            </div>
          </div>

          <template v-else>
            <v-alert v-if="limitReached" type="warning" text dense class="caption mb-2">
              {{ limitText }}
            </v-alert>
            <v-alert v-if="emptySelectionNames.length" type="warning" text dense class="caption mb-2">
              {{ emptySelectionText }}
            </v-alert>

            <div class="d-flex align-center caption grey--text text--darken-1 px-1 cloud-picker-table-header">
              <v-simple-checkbox
                :value="allVisibleSelected"
                :indeterminate="someVisibleSelected"
                @input="toggleSelectAll"
                class="mr-2"
                dense
              />
              <span class="d-flex align-center font-weight-medium" style="cursor: pointer" @click="toggleSort">
                {{ pickerText.name_column }}
                <v-icon small class="ml-1">{{ sortDir === "desc" ? "mdi-arrow-down" : "mdi-arrow-up" }}</v-icon>
              </span>
              <v-spacer />
              <span class="font-weight-medium" style="min-width: 70px; text-align: right">
                {{ pickerText.size_column }}
              </span>
            </div>

            <div class="cloud-picker-nav-list">
              <div
                v-for="item in filteredCurrentItems"
                :key="item.id"
                class="d-flex align-center px-1 cloud-picker-nav-row"
                :class="{ 'cloud-picker-nav-row--clickable': isNavigable(item) || (!item.is_folder && !item._sentinel && !item._placeholder) }"
                @click="onRowClick(item)"
              >
                <template v-if="item._placeholder">
                  <div class="caption grey--text font-italic py-1">{{ pickerText.folder_empty }}</div>
                </template>
                <template v-else-if="item._sentinel">
                  <div
                    v-intersect="sentinelHandler(item)"
                    class="d-flex align-center caption grey--text py-1"
                  >
                    <v-progress-circular indeterminate size="14" width="2" color="grey" class="mr-2" />
                    {{ pickerText.loading_more }}
                  </div>
                </template>
                <template v-else>
                  <span class="d-inline-flex mr-2" @click.stop>
                    <v-simple-checkbox
                      :value="selectedIds.includes(item.id)"
                      :disabled="item.disabled || isOversizedFile(item)"
                      dense
                      @input="toggleItem(item)"
                    />
                  </span>
                  <v-icon v-if="item.is_folder" small color="amber darken-2" class="mr-2">mdi-folder</v-icon>
                  <v-icon v-else small :color="getCloudFileColor(item.mime_type)" class="mr-2">
                    {{ getCloudFileIcon(item.mime_type) }}
                  </v-icon>
                  <span class="text-truncate" style="min-width: 0">{{ item.name }}</span>
                  <span
                    v-if="isOversizedFile(item)"
                    class="caption ml-2 error--text flex-shrink-0"
                  >
                    {{ pickerText.size_limit }}
                  </span>
                  <v-progress-circular
                    v-if="pendingIds.includes(item.id) || navLoadingId === item.id"
                    indeterminate
                    size="12"
                    width="2"
                    color="primary"
                    class="ml-2 flex-shrink-0"
                  />
                  <span
                    v-else-if="item.is_folder && selectedIds.includes(item.id)"
                    class="caption ml-2 primary--text flex-shrink-0"
                  >
                    {{ pickerText.folder_included }}
                  </span>
                  <v-spacer />
                  <span class="caption grey--text flex-shrink-0" style="min-width: 70px; text-align: right">
                    {{ !item.is_folder && item.size ? formatSize(item.size) : "" }}
                  </span>
                </template>
              </div>
              <div
                v-if="navLoading"
                class="d-flex align-center justify-center caption grey--text py-4"
              >
                <v-progress-circular indeterminate size="16" width="2" color="grey" class="mr-2" />
                {{ pickerText.loading_more }}
              </div>
            </div>
            <div
              v-if="searchQuery && !filteredCurrentItems.length"
              class="d-flex flex-column justify-center align-center grey--text py-10"
            >
              <v-icon size="40" color="grey lighten-1">mdi-file-search-outline</v-icon>
              <p class="mt-2 mb-0 text-center">{{ searchNoResultsText }}</p>
            </div>
          </template>
        </div>
      </v-card-text>

      <div v-if="footerItems.length" class="px-4 py-3 cloud-picker-selected-summary">
        <div class="d-flex align-center mb-2">
          <span class="body-2 font-weight-medium">{{ filesSelectedText }}</span>
          <v-spacer />
          <v-btn small text color="primary" class="text-none" @click="clearAll">
            {{ pickerText.clear_all }}
          </v-btn>
        </div>
        <div class="d-flex flex-wrap" style="gap: 6px; max-height: 108px; overflow-y: auto">
          <v-chip
            v-for="item in footerItems"
            :key="item.id"
            small
            label
            color="blue lighten-5"
            close
            close-icon="mdi-close"
            class="cloud-file-chip"
            :title="item.name"
            @click:close="removeSelected(item)"
          >
            <v-icon small left :color="item.is_folder ? 'amber darken-2' : getCloudFileColor(item.mime_type)">
              {{ item.is_folder ? "mdi-folder-outline" : getCloudFileIcon(item.mime_type) }}
            </v-icon>
            <span class="text-truncate">{{ item.name }}</span>
          </v-chip>
        </div>
      </div>

      <v-divider />

      <v-card-actions class="pa-3">
        <v-spacer />
        <v-btn color="error" outlined @click="close">
          {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
        </v-btn>
        <v-btn color="primary" :disabled="!footerItems.length" @click="confirm">
          {{ $vuetify.lang.t("$vuetify.gpt_dataset.cloud_picker.select_btn") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// Vuetify is installed without a `directives` arg, so v-intersect is not global.
import { Intersect } from "vuetify/lib/directives";
import { mapActions } from "vuex";

import getEnv from "@/config/env";
import {
  COMPLETE_CLOUD_STORAGE,
  CONNECT_CLOUD_STORAGE,
  DISCONNECT_CLOUD_STORAGE,
  GET_CLOUD_STORAGE_ACCOUNTS,
  GET_CLOUD_STORAGE_FILES,
  VALIDATE_CLOUD_SELECTION,
} from "@/store/_actiontypes";
import { getCloudFileIcon, getCloudFileColor } from "@/helpers/helper";
import { CLOUD_PROVIDERS } from "../../../mixins/datasetMixin";

const API_ORIGIN = new URL(getEnv("VUE_APP_PERSONALIZER_API")).origin;

const PAGE_SIZE = 50;
const SENTINEL_PREFIX = "__more__:";
const EMPTY_PREFIX = "__empty__:";
const MAX_SELECTION = 200;
const MAX_FILE_SIZE = 100 * 1024 * 1024;

export default {
  name: "CloudFilePicker",
  directives: { Intersect },
  props: {
    value: { type: Boolean, default: false },
    projectId: { type: String, required: true },
    provider: { type: String, required: true },
    // Lower-case extensions (no dot) the dataset accepts. Empty = no filtering.
    allowedExtensions: { type: Array, default: () => [] },
    existingItems: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      connecting: false,
      retrying: false,
      refreshing: false,
      oauthPending: false,
      oauthCancelled: false,
      popupOpen: false,
      callbackOrigin: null,
      treeItems: [],
      rootCursor: null,
      selectedIds: this.existingItems.map(i => i.id),
      removedExistingIds: [],
      pendingIds: [],
      limitReached: false,
      emptySelectionNames: [],
      accountId: null,
      accounts: [],
      accountIdsBeforeConnect: null,
      error: null,
      searchQuery: "",
      sortDir: "asc",
      itemCache: {},
      path: [],
      navLoading: false,
      navLoadingId: null,
      hasSeenItems: false,
      browseEpoch: 0,
      brokenAvatars: [],
    };
  },
  computed: {
    accountOptions() {
      return this.accounts.map(a => ({ text: a.user_id, value: a.user_id }));
    },
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    providerConfig() {
      return (
        CLOUD_PROVIDERS.find(p => p.value === this.provider) || {
          icon: "mdi-cloud",
          color: "primary",
          label: this.provider,
        }
      );
    },
    pickerText() {
      const label = this.providerConfig.label;
      const r = key => this.$vuetify.lang.t(`$vuetify.gpt_dataset.cloud_picker.${key}`).replace("{provider}", label);
      return {
        title: r("title"),
        loading: r("loading"),
        connecting: r("connecting"),
        oauth_pending_title: r("oauth_pending_title"),
        empty: r("empty"),
        fetch_error: r("fetch_error"),
        connect_error: r("connect_error"),
        popup_blocked: r("popup_blocked"),
        oauth_cancelled_description: r("oauth_cancelled_description"),
        oauth_cancelled_retry: r("oauth_cancelled_retry"),
        loading_more: r("loading_more"),
        folder_included: r("folder_included"),
        no_supported_files: r("no_supported_files"),
        size_limit: r("size_limit").replace("{max}", MAX_FILE_SIZE / 1024 / 1024),
        folder_empty: r("folder_empty"),
        selection_limit: r("selection_limit"),
        add_account: r("add_account"),
        refresh_btn: r("refresh_btn"),
        select_hint: r("select_hint"),
        select_hint_mobile: r("select_hint_mobile"),
        search_placeholder: r("search_placeholder"),
        clear_all: r("clear_all"),
        name_column: r("name_column"),
        size_column: r("size_column"),
      };
    },
    allowedTypesLabel() {
      return this.allowedExtensions.map(ext => `.${String(ext).replace(/^\./, "").toLowerCase()}`).join(", ");
    },
    allowedHintText() {
      return this.$vuetify.lang
        .t("$vuetify.gpt_dataset.cloud_picker.allowed_hint")
        .replace("{types}", this.allowedTypesLabel);
    },
    selectHintText() {
      return this.$vuetify.breakpoint.xs ? this.pickerText.select_hint_mobile : this.pickerText.select_hint;
    },
    hintTooltip() {
      if (!this.$vuetify.breakpoint.xs) return this.allowedHintText;
      return this.$vuetify.lang
        .t("$vuetify.gpt_dataset.cloud_picker.formats_supported_hint")
        .replace("{types}", this.allowedTypesLabel);
    },
    limitText() {
      return this.pickerText.selection_limit.replace("{max}", MAX_SELECTION);
    },
    emptySelectionText() {
      return this.pickerText.no_supported_files.replace("{folders}", this.emptySelectionNames.join(", "));
    },
    emptyFilteredText() {
      return this.$vuetify.lang
        .t("$vuetify.gpt_dataset.cloud_picker.empty_filtered")
        .replace("{types}", this.allowedTypesLabel)
        .replace("{provider}", this.providerConfig.label);
    },
    footerItems() {
      const selected = new Set(this.selectedIds);
      const result = [];
      const visited = new Set();
      const walk = (items, covered) => {
        items.forEach(item => {
          if (item._sentinel || item._placeholder) return;
          visited.add(item.id);
          const isSelected = selected.has(item.id);
          if (isSelected && !covered) result.push(item);
          if (item.is_folder && item.children?.length) {
            walk(item.children, covered || isSelected);
          }
        });
      };
      walk(this.treeItems, false);
      const removed = new Set(this.removedExistingIds);
      const carried = this.existingItems.filter(item => !visited.has(item.id) && !removed.has(item.id));
      const carriedIds = new Set(carried.map(i => i.id));
      const cached = this.selectedIds
        .map(id => this.itemCache[id])
        .filter(item => item && !visited.has(item.id) && !removed.has(item.id) && !carriedIds.has(item.id));
      return [...result, ...carried, ...cached];
    },
    searchNoResultsText() {
      return this.$vuetify.lang
        .t("$vuetify.gpt_dataset.cloud_picker.search_no_results")
        .replace("{query}", this.searchQuery.trim());
    },
    filesSelectedText() {
      return this.$vuetify.lang
        .t("$vuetify.gpt_dataset.cloud_picker.files_selected")
        .replace("{count}", this.footerItems.length);
    },
    accountInitial() {
      return (this.accountId || "?").charAt(0).toUpperCase();
    },
    accountPicture() {
      const acc = this.accounts.find(a => a.user_id === this.accountId);
      return acc ? this.pictureFor(acc) : null;
    },
    avatarEnabled() {
      return !["box", "onedrive"].includes(this.provider);
    },
    searchTerm() {
      return (this.searchQuery || "").trim().toLowerCase();
    },
    rootLabel() {
      const labels = {
        googledrive: "My Drive",
        onedrive: "Files",
        dropbox: "All files",
        box: "All Files",
      };
      return labels[this.provider] || this.providerConfig.label || "Files";
    },
    breadcrumbItems() {
      return [{ id: null, name: this.rootLabel }, ...this.path];
    },
    displayCrumbs() {
      const MAX_VISIBLE = 4;
      const MAX_NAME_LEN = 20;
      const crumbs = this.breadcrumbItems;
      const lastIdx = crumbs.length - 1;
      const toCrumb = (crumb, index) => {
        const isRoot = index === 0;
        const name =
          !isRoot && crumb.name.length > MAX_NAME_LEN
            ? `${crumb.name.slice(0, MAX_NAME_LEN).trimEnd()}...`
            : crumb.name;
        return {
          key: crumb.id || `crumb-${index}`,
          name,
          title: crumb.name,
          index,
          last: index === lastIdx,
        };
      };
      if (crumbs.length <= MAX_VISIBLE) return crumbs.map(toCrumb);
      const tailStart = lastIdx - 1;
      const hidden = crumbs.slice(1, tailStart);
      return [
        toCrumb(crumbs[0], 0),
        {
          key: "crumb-ellipsis",
          name: "...",
          title: hidden.map(c => c.name).join(" / "),
          index: tailStart - 1,
          last: true,
        },
        ...crumbs.slice(tailStart).map(c => toCrumb(c, crumbs.indexOf(c))),
      ];
    },
    showSearch() {
      const hasItems = this.currentItems.some(item => !item._sentinel && !item._placeholder);
      return this.hasSeenItems && (hasItems || this.navLoading || !!this.navLoadingId);
    },
    currentFolder() {
      if (!this.path.length) return null;
      return this.findNode(this.treeItems, this.path[this.path.length - 1].id);
    },
    currentItems() {
      const list = this.currentFolder ? this.currentFolder.children || [] : this.treeItems;
      return this.sortItems(list);
    },
    filteredCurrentItems() {
      if (!this.searchTerm) return this.currentItems;
      return this.currentItems.filter(
        item => !item._sentinel && this.matchesSearch(item, this.searchTerm)
      );
    },
    visibleSelectableIds() {
      return this.filteredCurrentItems
        .filter(item => !item._sentinel && !item._placeholder && !item.disabled && !this.isOversizedFile(item))
        .map(item => item.id);
    },
    allVisibleSelected() {
      return (
        this.visibleSelectableIds.length > 0 && this.visibleSelectableIds.every(id => this.selectedIds.includes(id))
      );
    },
    someVisibleSelected() {
      return !this.allVisibleSelected && this.visibleSelectableIds.some(id => this.selectedIds.includes(id));
    },
  },
  watch: {
    currentItems(items) {
      if (items.length) this.hasSeenItems = true;
    },
    value(val) {
      if (val) {
        this.reset();
        this.fetchFiles();
      }
    },
    selectedIds(ids, previous) {
      // The revert below re-enters this watcher; that pass is not a user action.
      if (this.revertingSelection) {
        this.revertingSelection = false;
        return;
      }
      if (this.syncingCoverage) {
        this.syncingCoverage = false;
        return;
      }
      const meaningfulCount = ids.filter(id => !this.cascadeIds.has(id)).length;
      if (meaningfulCount > MAX_SELECTION) {
        this.limitReached = true;
        this.revertingSelection = true;
        this.$nextTick(() => {
          this.selectedIds = previous;
        });
        return;
      }
      // The warning describes the selection as it stands, not as it once was.
      this.limitReached = false;
      const added = ids.filter(id => !previous.includes(id));
      if (added.length) this.validateSelection(added);
      this.syncCoverage();
    },
  },
  created() {
    this.revertingSelection = false;
    this.syncingCoverage = false;
    this.inFlightIds = new Set();
    this.validatedIds = new Set();
    this.knownEmptyIds = new Set();
    this.cascadeIds = new Set();
    this.sentinelHandlers = {};
  },
  mounted() {
    this.oAuthMessageHandler = this.handleOAuthMessage;
    window.addEventListener("message", this.oAuthMessageHandler);
    if (this.value) this.fetchFiles();
  },
  beforeDestroy() {
    this.clearPopupWatch();
    this.closeAuthWindow();
    window.removeEventListener("message", this.oAuthMessageHandler);
  },
  methods: {
    ...mapActions("project", [
      CONNECT_CLOUD_STORAGE,
      GET_CLOUD_STORAGE_FILES,
      GET_CLOUD_STORAGE_ACCOUNTS,
      COMPLETE_CLOUD_STORAGE,
      DISCONNECT_CLOUD_STORAGE,
      VALIDATE_CLOUD_SELECTION,
    ]),

    async handleOAuthMessage(event) {
      const data = event.data;
      if (!data || data.source !== "gigalogy-cloud-storage") return;

      const expected = this.callbackOrigin || API_ORIGIN;
      if (event.origin !== expected) {
        console.warn(`[CloudFilePicker] ignored OAuth message from ${event.origin}; expected ${expected}`);
        return;
      }

      this.clearPopupWatch();
      this.popupOpen = false;
      this.closeAuthWindow();

      if (!data.success || !data.link_code) {
        this.oauthCancelled = true;
        this.oauthPending = true;
        return;
      }

      this.retrying = true;
      try {
        await this.COMPLETE_CLOUD_STORAGE({
          project_id: this.projectId,
          link_code: data.link_code,
        });
        this.oauthPending = false;
        this.oauthCancelled = false;
        await this.fetchFiles();
      } catch (err) {
        this.oauthPending = true;
        this.error = this.errorMessage(err, this.pickerText.connect_error);
      } finally {
        this.retrying = false;
      }
    },
    errorMessage(err, fallback) {
      return err.response?.data?.detail?.message || err.message || fallback;
    },

    // The popup watcher outlives any single dialog session unless stopped, so
    // every exit path (reset, confirm, close, destroy) has to clear it.
    clearPopupWatch() {
      clearInterval(this.popupInterval);
      this.popupInterval = null;
    },

    closeAuthWindow() {
      // Dropping the reference leaves the window open on the user's screen.
      if (this.authWindow && !this.authWindow.closed) {
        this.authWindow.close();
      }
      this.authWindow = null;
    },

    reset() {
      this.clearPopupWatch();
      this.browseEpoch += 1;
      this.inFlightIds.clear();
      this.validatedIds.clear();
      this.knownEmptyIds.clear();
      this.cascadeIds.clear();
      this.pendingIds = [];
      this.limitReached = false;
      this.emptySelectionNames = [];
      this.treeItems = [];
      this.rootCursor = null;
      this.selectedIds = this.existingItems.map(i => i.id);
      this.removedExistingIds = [];
      this.accountId = null;
      this.error = null;
      this.oauthPending = false;
      this.oauthCancelled = false;
      this.popupOpen = false;
      this.callbackOrigin = null;
      this.searchQuery = "";
      this.sortDir = "asc";
      this.itemCache = {};
      this.path = [];
      this.navLoading = false;
      this.navLoadingId = null;
      this.hasSeenItems = false;
      this.closeAuthWindow();
    },
    resetBrowseState() {
      this.browseEpoch += 1;
      this.path = [];
      this.treeItems = [];
      this.rootCursor = null;
      // Stale nodes from the previous account would fail the "gone" check in
      // appendPage and mark existing selections as removed.
      this.itemCache = {};
      const removed = new Set(this.removedExistingIds);
      this.selectedIds = this.existingItems.filter(i => !removed.has(i.id)).map(i => i.id);
      this.pendingIds = [];
      this.emptySelectionNames = [];
      this.limitReached = false;
      this.hasSeenItems = false;
      this.searchQuery = "";
      this.inFlightIds.clear();
      this.validatedIds.clear();
      this.knownEmptyIds.clear();
      this.cascadeIds.clear();
    },

    async fetchFiles(isRetry = false) {
      let epoch = this.browseEpoch;
      this.loading = true;
      this.error = null;
      this.oauthPending = false;
      if (!isRetry) this.oauthCancelled = false;
      try {
        const accountId = await this.resolveAccountId();
        // The dialog was closed and reopened (or the account switched) while
        // this was in flight — a newer call already owns the browse state.
        if (epoch !== this.browseEpoch) return;

        if (!accountId) {
          if (isRetry) {
            this.oauthCancelled = true;
            this.oauthPending = true;
            return;
          }
          await this.connectAndWait();
          return;
        }

        this.accountId = accountId;
        this.resetBrowseState();
        // resetBrowseState() just bumped the epoch itself — this call still owns browsing, so adopt the new value instead of treating itself as superseded.
        epoch = this.browseEpoch;
        await this.loadRootPage();
      } catch (err) {
        if (epoch !== this.browseEpoch) return;
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          if (isRetry) {
            this.oauthCancelled = true;
            this.oauthPending = true;
          } else {
            await this.connectAndWait();
          }
        } else {
          this.error = this.errorMessage(err, this.pickerText.fetch_error);
        }
      } finally {
        if (epoch === this.browseEpoch) this.loading = false;
      }
    },

    async resolveAccountId() {
      const accountsRes = await this.GET_CLOUD_STORAGE_ACCOUNTS({
        project_id: this.projectId,
        provider: this.provider,
      });
      const accounts = accountsRes.data?.detail?.accounts || accountsRes.data?.detail || accountsRes.data || [];
      if (!Array.isArray(accounts)) return null;

      const usable = accounts.filter(a => a?.status !== "revoked");
      this.accounts = usable;
      if (!usable.length) return null;

      // Just came back from connecting another account — browse that one.
      if (this.accountIdsBeforeConnect) {
        const added = usable.find(a => !this.accountIdsBeforeConnect.includes(a.user_id));
        this.accountIdsBeforeConnect = null;
        if (added) return added.user_id;
      }

      const lastUsed = usable.find(
        a => a.user_id === accountsRes.data?.detail?.last_account_id
      );
      const current = usable.find(a => a.user_id === this.accountId);
      return (current || lastUsed || usable[0]).user_id;
    },

    async switchAccount(userId) {
      if (!userId || userId === this.accountId) return;
      this.accountId = userId;
      this.resetBrowseState();
      this.error = null;
      this.loading = true;
      try {
        await this.loadRootPage();
      } catch (err) {
        this.error = this.errorMessage(err, this.pickerText.fetch_error);
      } finally {
        this.loading = false;
      }
    },

    async addAnotherAccount() {
      this.accountIdsBeforeConnect = this.accounts.map(a => a.user_id);
      await this.connectAndWait();
    },

    async disconnectAccount(userId) {
      let epoch = this.browseEpoch;
      try {
        await this.DISCONNECT_CLOUD_STORAGE({
          project_id: this.projectId,
          provider: this.provider,
          account_id: userId,
        });
        // The dialog was closed and reopened (or the account switched) while
        // this was in flight — a newer call already owns the browse state.
        if (epoch !== this.browseEpoch) return;
        this.accounts = this.accounts.filter(a => a.user_id !== userId);
        // If the account we were browsing is gone, start from whatever is left.
        if (userId === this.accountId) this.accountId = null;
        if (!this.accounts.length) {
          this.resetBrowseState();
          await this.connectAndWait();
          return;
        }
        this.loading = true;
        try {
          const next = await this.resolveAccountId();
          if (epoch !== this.browseEpoch) return;
          // An unrelated account was disconnected — keep the current browse.
          if (next && next === this.accountId) return;
          this.accountId = next;
          this.resetBrowseState();
          // Same self-bump as in fetchFiles: adopt the new epoch rather than treating this call as superseded by its own reset.
          epoch = this.browseEpoch;
          if (next) await this.loadRootPage();
        } finally {
          if (epoch === this.browseEpoch) this.loading = false;
        }
      } catch (err) {
        if (epoch !== this.browseEpoch) return;
        this.error = this.errorMessage(err, this.pickerText.connect_error);
      }
    },

    async connectAndWait() {
      this.connecting = true;
      try {
        const res = await this.CONNECT_CLOUD_STORAGE({
          project_id: this.projectId,
          provider: this.provider,
        });
        const authUrl = res.data?.detail?.auth_url || res.data?.auth_url || res.data;
        this.callbackOrigin = res.data?.detail?.callback_origin || null;
        if (authUrl && typeof authUrl === "string") {
          const authWindow = window.open(authUrl, "_blank", "width=600,height=700");
          // A blocked popup returns null. Nothing will ever close it, so the
          // watcher can't clear popupOpen and the retry button would spin
          // forever — fall back to the error state, which offers a real retry.
          if (!authWindow) {
            this.clearPopupWatch();
            this.oauthPending = false;
            this.popupOpen = false;
            this.error = this.pickerText.popup_blocked;
            return;
          }
          this.authWindow = authWindow;
          this.oauthPending = true;
          this.popupOpen = true;
          this.watchPopup(authWindow);
        } else {
          this.error = this.pickerText.connect_error;
        }
      } catch (err) {
        this.error = this.errorMessage(err, this.pickerText.connect_error);
      } finally {
        this.connecting = false;
      }
    },

    watchPopup(win) {
      if (!win) return;
      this.clearPopupWatch();
      const interval = setInterval(() => {
        if (win.closed) {
          clearInterval(interval);
          this.popupOpen = false;
          if (this.oauthPending && this.dialog) {
            this.retryFetch();
          }
        }
      }, 800);
      this.popupInterval = interval;
    },

    async retryFetch() {
      this.retrying = true;
      await this.fetchFiles(true);
      this.retrying = false;
    },
    async handleOAuthRetry() {
      this.retrying = true;
      this.oauthCancelled = false;
      try {
        const accountId = await this.resolveAccountId();

        if (!accountId) {
          await this.connectAndWait();
          return;
        }

        this.accountId = accountId;
        this.resetBrowseState();
        this.oauthPending = false;
        this.loading = true;
        try {
          await this.loadRootPage();
        } finally {
          this.loading = false;
        }
      } catch (err) {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          this.oauthCancelled = true;
          this.oauthPending = true;
        } else {
          this.error = this.errorMessage(err, this.pickerText.fetch_error);
          this.oauthPending = false;
        }
      } finally {
        this.retrying = false;
      }
    },

    async fetchPage(folderId, cursor, bustCache = false) {
      const res = await this.GET_CLOUD_STORAGE_FILES({
        project_id: this.projectId,
        provider: this.provider,
        account_id: this.accountId,
        folder_id: folderId || undefined,
        cursor: cursor || undefined,
        limit: PAGE_SIZE,
        extensions: this.allowedExtensions.length ? this.allowedExtensions.join(",") : undefined,
        bust_cache: bustCache || undefined,
      });
      const detail = res.data?.detail || {};
      return {
        files: detail.files || [],
        nextCursor: detail.next_cursor || null,
      };
    },

    toNode(entry) {
      if (entry.is_folder) {
        return { ...entry, children: [], _cursor: null, loaded: false };
      }
      return { ...entry };
    },

    sentinelFor(folderId) {
      return {
        id: `${SENTINEL_PREFIX}${folderId || "root"}`,
        name: "",
        is_folder: false,
        disabled: true,
        _sentinel: true,
        _folderId: folderId || null,
      };
    },

    appendPage(list, folderId, files, nextCursor) {
      const parentId = folderId || null;
      const existing = list.filter(n => !n._sentinel);
      const added = files.filter(e => !(e.is_folder && e.child_count === 0)).map(e => {
        const node = this.toNode(e);
        this.itemCache[node.id] = { ...node, _parentId: parentId };
        return node;
      });
      const next = existing.concat(added);
      if (nextCursor) {
        next.push(this.sentinelFor(folderId));
      } else {
        const liveIds = new Set(next.map(n => n.id));
        const gone = this.selectedIds.filter(
          id => this.itemCache[id]?._parentId === parentId && !liveIds.has(id)
        );
        if (gone.length) {
          const goneSet = new Set(gone);
          this.selectedIds = this.selectedIds.filter(id => !goneSet.has(id));
          this.removedExistingIds = [...new Set([...this.removedExistingIds, ...gone])];
          gone.forEach(id => this.$delete(this.itemCache, id));
        }
      }
      return next;
    },

    async loadRootPage() {
      const epoch = this.browseEpoch;
      const { files, nextCursor } = await this.fetchPage(null, this.rootCursor);
      // A reset or account switch happened while this page was in flight.
      if (epoch !== this.browseEpoch) return;
      this.treeItems = this.appendPage(this.treeItems, null, files, nextCursor);
      this.rootCursor = nextCursor;
      this.resyncSelection();
    },

    // Ids of every non-sentinel node in a subtree, so a refresh can drop
    // exactly the bookkeeping for what it's about to discard.
    collectIds(items) {
      const ids = [];
      const walk = list => {
        (list || []).forEach(item => {
          if (item._sentinel || item._placeholder) return;
          ids.push(item.id);
          if (item.children?.length) walk(item.children);
        });
      };
      walk(items);
      return ids;
    },

    async refreshFiles() {
      if (this.refreshing || this.loading) return;
      this.refreshing = true;
      this.error = null;
      try {
        // Refresh the folder the user is actually looking at, not the root.
        // `currentFolder` resolves the live tree node (path entries are just
        // {id, name} breadcrumbs) so mutating it below updates what's rendered.
        const currentFolder = this.currentFolder;
        const folderId = currentFolder ? currentFolder.id : null;
        const epoch = this.browseEpoch;
        const { files, nextCursor } = await this.fetchPage(folderId, null, true);
        if (epoch !== this.browseEpoch) return;

        const stale = new Set(
          this.collectIds(currentFolder ? currentFolder.children : this.treeItems)
        );
        stale.forEach(id => {
          this.inFlightIds.delete(id);
          this.validatedIds.delete(id);
          this.knownEmptyIds.delete(id);
          this.cascadeIds.delete(id);
        });
        this.pendingIds = this.pendingIds.filter(id => !stale.has(id));

        if (currentFolder) {
          currentFolder.children = this.appendPage([], folderId, files, nextCursor);
          currentFolder._cursor = nextCursor;
          currentFolder.loaded = true;
          this.$nextTick(() => this.markIfEmpty(currentFolder));
        } else {
          this.treeItems = this.appendPage([], null, files, nextCursor);
          this.rootCursor = nextCursor;
        }
        this.resyncSelection();
      } catch (err) {
        this.error = this.errorMessage(err, this.pickerText.fetch_error);
      } finally {
        this.refreshing = false;
      }
    },
    async loadChildren(folder) {
      if (folder.loaded || this.inFlightIds.has(folder.id)) return;
      this.inFlightIds.add(folder.id);
      const epoch = this.browseEpoch;
      try {
        const { files, nextCursor } = await this.fetchPage(folder.id, null);
        if (epoch !== this.browseEpoch) return;
        folder.children = this.appendPage([], folder.id, files, nextCursor);
        folder._cursor = nextCursor;
        folder.loaded = true;
        // Deferred: pruning inside load-children yanks the node out from under it.
        this.$nextTick(() => this.markIfEmpty(folder));
        this.resyncSelection();
      } catch (err) {
        folder.loaded = false;
        this.error = this.errorMessage(err, this.pickerText.fetch_error);
      } finally {
        this.inFlightIds.delete(folder.id);
      }
    },

    // Per folder: two sentinels can be visible, and there is no second intersection.
    async loadMore(folderId) {
      const key = folderId || "root";
      if (this.inFlightIds.has(key)) return;
      this.inFlightIds.add(key);
      try {
        if (!folderId) {
          if (this.rootCursor) await this.loadRootPage();
          return;
        }
        const folder = this.findNode(this.treeItems, folderId);
        if (!folder || !folder._cursor) return;
        const epoch = this.browseEpoch;
        const { files, nextCursor } = await this.fetchPage(folderId, folder._cursor);
        if (epoch !== this.browseEpoch) return;
        folder.children = this.appendPage(folder.children, folderId, files, nextCursor);
        folder._cursor = nextCursor;
        this.resyncSelection();
      } catch (err) {
        this.error = this.errorMessage(err, this.pickerText.fetch_error);
      } finally {
        this.inFlightIds.delete(key);
      }
    },

    sentinelHandler(sentinel) {
      if (!this.sentinelHandlers[sentinel.id]) {
        this.sentinelHandlers[sentinel.id] = (entries, observer, isIntersecting) => {
          if (isIntersecting) this.loadMore(sentinel._folderId);
        };
      }
      return this.sentinelHandlers[sentinel.id];
    },

    // Listing-time pruning is the server's job; a folder shown because it had a
    // subfolder must not vanish once that subfolder turns out to be a dead end.
    markIfEmpty(folder) {
      if (folder._cursor || folder.children.length) return;
      this.knownEmptyIds.add(folder.id);
      this.$set(folder, "disabled", true);
      this.selectedIds = this.selectedIds.filter(id => id !== folder.id);
      if (!this.removedExistingIds.includes(folder.id)) {
        this.removedExistingIds = [...this.removedExistingIds, folder.id];
      }
      folder.children = [
        {
          id: `${EMPTY_PREFIX}${folder.id}`,
          name: "",
          is_folder: false,
          disabled: true,
          _placeholder: true,
        },
      ];
    },

    findNode(items, id) {
      for (const item of items) {
        if (item.id === id) return item;
        if (item.children?.length) {
          const found = this.findNode(item.children, id);
          if (found) return found;
        }
      }
      return null;
    },

    removeSelected(item) {
      this.selectedIds = this.selectedIds.filter(id => id !== item.id);
      if (!this.removedExistingIds.includes(item.id)) {
        this.removedExistingIds = [...this.removedExistingIds, item.id];
      }
    },

    clearAll() {
      this.selectedIds = [];
      this.removedExistingIds = [...new Set([...this.removedExistingIds, ...this.existingItems.map(i => i.id)])];
    },

    // The tick is held, not applied then revoked: a folder only appears
    // selected once its subtree is confirmed to contain an ingestible file.
    async validateSelection(ids) {
      const nodes = ids.map(id => this.findNode(this.treeItems, id)).filter(node => node?.is_folder);
      if (!nodes.length) return;

      if (!this.pendingIds.length) this.emptySelectionNames = [];
      const known = nodes.filter(n => this.knownEmptyIds.has(n.id));
      if (known.length) this.rejectFolders(known);
      this.syncCoverage();

      const unknown = nodes.filter(n => !this.knownEmptyIds.has(n.id) && !this.validatedIds.has(n.id));
      if (!unknown.length) return;

      this.limitReached = false;
      const held = new Set(unknown.map(n => n.id));
      this.pendingIds = [...this.pendingIds, ...held];
      this.selectedIds = this.selectedIds.filter(id => !held.has(id));
      unknown.forEach(n => this.$set(n, "disabled", true));

      const epoch = this.browseEpoch;
      try {
        const res = await this.VALIDATE_CLOUD_SELECTION({
          project_id: this.projectId,
          provider: this.provider,
          account_id: this.accountId,
          folder_ids: unknown.map(n => n.id).join(","),
          extensions: this.allowedExtensions.length ? this.allowedExtensions.join(",") : undefined,
        });
        if (epoch !== this.browseEpoch) return;
        const empty = new Set(res.data?.detail?.empty_folder_ids || []);
        empty.forEach(id => this.knownEmptyIds.add(id));
        const usable = unknown.filter(n => !empty.has(n.id));
        usable.forEach(n => this.validatedIds.add(n.id));
        if (usable.length) {
          this.selectedIds = [...this.selectedIds, ...usable.map(n => n.id)];
        }
        if (empty.size) {
          this.rejectFolders(unknown.filter(n => empty.has(n.id)));
        }
      } catch (err) {
        if (epoch !== this.browseEpoch) return;
        // Undecided: honour the tick. Marking them settled stops the re-add from
        // re-entering this watcher in a loop.
        unknown.forEach(n => this.validatedIds.add(n.id));
        this.selectedIds = [...this.selectedIds, ...unknown.map(n => n.id)];
      } finally {
        this.pendingIds = this.pendingIds.filter(id => !held.has(id));
        unknown.filter(n => !this.knownEmptyIds.has(n.id)).forEach(n => this.$set(n, "disabled", false));
        this.syncCoverage();
      }
    },

    rejectFolders(nodes) {
      const ids = new Set(nodes.map(n => n.id));
      this.selectedIds = this.selectedIds.filter(id => !ids.has(id));
      nodes.forEach(n => this.$set(n, "disabled", true));
      // Without this, resetBrowseState() would resurrect a server-confirmed-empty
      // existing selection on the next account switch or refresh (see markFolderKnownEmpty).
      this.removedExistingIds = [...new Set([...this.removedExistingIds, ...nodes.map(n => n.id)])];
      this.emptySelectionNames = [...new Set([...this.emptySelectionNames, ...nodes.map(n => n.name)])];
      this.syncCoverage();
    },
    syncCoverage() {
      const additions = [];
      const removals = [];
      const selected = new Set(this.selectedIds);
      const walk = (items, covered) => {
        items.forEach(item => {
          if (item._sentinel || item._placeholder) return;
          const ownPick = !covered && selected.has(item.id) && !this.cascadeIds.has(item.id);
          if (covered) {
            if (!this.cascadeIds.has(item.id)) {
              this.cascadeIds.add(item.id);
              additions.push(item.id);
            }
            if (!item.disabled) this.$set(item, "disabled", true);
          } else if (this.cascadeIds.has(item.id)) {
            this.cascadeIds.delete(item.id);
            removals.push(item.id);
            this.$set(item, "disabled", false);
          }
          if (item.children?.length) walk(item.children, covered || ownPick);
        });
      };
      walk(this.treeItems, false);
      if (additions.length || removals.length) {
        const drop = new Set(removals);
        this.syncingCoverage = true;
        this.selectedIds = [...new Set([...this.selectedIds.filter(id => !drop.has(id)), ...additions])];
      }
    },
    resyncSelection() {
      this.$nextTick(() => {
        this.syncCoverage();
      });
    },

    confirm() {
      if (!this.footerItems.length) return;
      if (this.footerItems.some(item => this.isOversizedFile(item))) {
        this.$store.dispatch("alert/ADD_ALERT", {
          message: `${this.$vuetify.lang.t("$vuetify.gpt_dataset.file_size_exceeded_error")} ${MAX_FILE_SIZE / 1024 / 1024}MB`,
          color: "warning",
        });
        return;
      }
      this.clearPopupWatch();
      this.closeAuthWindow();
      const items = this.footerItems.map(item => ({
        id: item.id,
        name: item.name,
        mime_type: item.mime_type || null,
        size: item.size || 0,
        is_folder: item.is_folder || false,
        account_id: item.account_id || this.accountId,
      }));
      this.$emit("select", items);
      this.dialog = false;
    },

    close() {
      this.clearPopupWatch();
      this.closeAuthWindow();
      this.$emit("cancel");
      this.dialog = false;
    },

    getCloudFileIcon,
    getCloudFileColor,

    formatSize(bytes) {
      if (!bytes) return "";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    },

    toggleSelectAll(checked) {
      if (checked) {
        const toAdd = this.visibleSelectableIds.filter(id => !this.selectedIds.includes(id));
        this.selectedIds = [...this.selectedIds, ...toAdd];
      } else {
        const remove = new Set(this.visibleSelectableIds);
        this.selectedIds = this.selectedIds.filter(id => !remove.has(id));
      }
    },

    matchesSearch(item, search) {
      if (item._sentinel || item._placeholder) return false;
      return (item.name || "").toLowerCase().includes(search);
    },

    sortItems(items) {
      const direction = this.sortDir === "desc" ? -1 : 1;
      const real = items.filter(item => !item._sentinel);
      const sentinels = items.filter(item => item._sentinel);
      const sorted = [...real].sort((a, b) => {
        if (!!a.is_folder !== !!b.is_folder) return a.is_folder ? -1 : 1;
        return direction * (a.name || "").localeCompare(b.name || "");
      });
      return [...sorted, ...sentinels];
    },

    isNavigable(item) {
      return item.is_folder && !item._sentinel && !item._placeholder && !item.disabled;
    },

    pictureFor(acc) {
      if (!acc || !this.avatarEnabled || !acc.picture) return null;
      if (this.brokenAvatars.includes(acc.user_id)) return null;
      return acc.picture;
    },

    markAvatarBroken(userId) {
      if (!this.brokenAvatars.includes(userId)) this.brokenAvatars.push(userId);
    },

    async enterFolder(folder) {
      if (!this.isNavigable(folder)) return;
      this.path = [...this.path, { id: folder.id, name: folder.name }];
      this.searchQuery = "";
      if (!folder.loaded) {
        this.navLoading = true;
        this.navLoadingId = folder.id;
        try {
          await this.loadChildren(folder);
        } finally {
          this.navLoading = false;
          this.navLoadingId = null;
        }
      }
    },

    navigateTo(index) {
      this.path = this.path.slice(0, index);
      this.searchQuery = "";
    },

    onRowClick(item) {
      if (item._sentinel || item._placeholder || item.disabled) return;
      if (item.is_folder) {
        this.enterFolder(item);
        return;
      }
      this.toggleItem(item);
    },

    isOversizedFile(item) {
      return !item.is_folder && !item._sentinel && !item._placeholder && item.size > MAX_FILE_SIZE;
    },

    toggleItem(item) {
      if (item.disabled || this.isOversizedFile(item)) return;
      if (this.selectedIds.includes(item.id)) {
        this.selectedIds = this.selectedIds.filter(id => id !== item.id);
      } else {
        this.selectedIds = [...this.selectedIds, item.id];
      }
    },

    toggleSort() {
      this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
    },
  },
};
</script>

<style scoped>
.v-card ::v-deep .v-card__title {
  padding-bottom: 0;
}
.v-card ::v-deep .v-card__title + .v-card__text {
  padding-top: 0;
}
.cloud-picker-band-divider {
  margin-left: -16px;
  margin-right: -16px;
  max-width: none;
}
.cloud-picker-refresh-spin {
  animation: cloud-picker-refresh-spin 0.9s linear infinite;
}
@keyframes cloud-picker-refresh-spin {
  to {
    transform: rotate(360deg);
  }
}
.cloud-picker-account-row {
  background-color: rgba(0, 0, 0, 0.03);
  /* Stretch past the card-text side padding so the band runs edge to edge. */
  margin-left: -16px;
  margin-right: -16px;
  padding-left: 16px;
  padding-right: 16px;
}
.cloud-picker-table-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 6px;
  margin-bottom: 4px;
}
.cloud-picker-breadcrumb {
  overflow: hidden;
}
.cloud-picker-crumb {
  max-width: 160px;
  min-width: 0;
}
.cloud-picker-crumb--root {
  max-width: none;
}
.cloud-picker-crumb--link {
  cursor: pointer;
}
.cloud-picker-crumb--link:hover {
  text-decoration: underline;
}
.cloud-picker-nav-row {
  border-radius: 4px;
  min-height: 32px;
}
.cloud-picker-nav-row--clickable {
  cursor: pointer;
}
.cloud-picker-nav-row--clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
.info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #333;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 8px;
  pointer-events: auto;
}
@media (max-width: 599px) {
  .cloud-picker-toolbar {
    flex-wrap: wrap;
  }
  .cloud-picker-actions {
    flex-basis: 100%;
    justify-content: space-between;
    margin-top: 6px;
    margin-left: 0;
  }
  .cloud-picker-searchbox {
    flex: 1;
    width: auto;
    margin-right: 8px;
  }
}
.cloud-picker-searchbox {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  width: 200px;
  height: 32px;
  border-radius: 16px;
  background-color: rgba(0, 0, 0, 0.06);
  transition: background-color 0.15s ease;
}
.cloud-picker-searchbox:focus-within {
  background-color: rgba(0, 0, 0, 0.1);
}
.cloud-picker-searchbox-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  padding: 0;
}
.cloud-picker-searchbox-input::placeholder {
  color: rgba(0, 0, 0, 0.42);
}
.cloud-picker-searchbox-clear {
  cursor: pointer;
}
.cloud-picker-searchbox-clear:hover {
  color: rgba(0, 0, 0, 0.7) !important;
}
.cloud-picker-selected-summary {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.cloud-picker-account {
  flex: 1 1 auto;
  min-width: 0;
}
.cloud-picker-add-account--stacked .cloud-picker-add-account-icon {
  width: 28px;
  margin-right: 10px;
}
.cloud-file-chip {
  max-width: 100%;
}
.cloud-file-chip >>> .v-chip__content {
  min-width: 0;
  max-width: 100%;
}
.cloud-file-chip >>> .v-icon {
  flex-shrink: 0;
}
</style>
